import React, { useState, useEffect, useRef } from 'react';
import DOMPurify from 'dompurify';
import { useLanguage } from '../context/LanguageContext';
import { Mail, Upload, Send, Users, TrendingUp, ArrowLeft, Eye, Loader2, CheckCircle, XCircle, Clock } from 'lucide-react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminNewsletter = () => {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('subscribers');
  
  // Import form
  const [importText, setImportText] = useState('');
  const [importing, setImporting] = useState(false);
  
  // Compose form
  const [emailSubject, setEmailSubject] = useState('');
  const [emailContent, setEmailContent] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState('new_site');
  const [sending, setSending] = useState(false);
  
  // Campaign tracking
  const [activeCampaign, setActiveCampaign] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const pollRef = useRef(null);
  
  // Preview
  const [previewHtml, setPreviewHtml] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [testSending, setTestSending] = useState(false);

  useEffect(() => {
    fetchStats();
    fetchSubscribers();
    fetchCampaigns();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchStats = async () => {
    try {
      const response = await fetch(`${API_URL}/api/newsletter/stats`);
      const data = await response.json();
      setStats(data);
    } catch (error) {
    }
  };

  const fetchSubscribers = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/newsletter/subscribers?active_only=false`);
      const data = await response.json();
      if (data.success) setSubscribers(data.subscribers);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const fetchCampaigns = async () => {
    try {
      const response = await fetch(`${API_URL}/api/newsletter/campaigns`);
      const data = await response.json();
      setCampaigns(data.campaigns || []);
    } catch (error) {
    }
  };

  const handleImport = async () => {
    if (!importText.trim()) {
      toast.error('Inserisci almeno una email');
      return;
    }
    setImporting(true);
    try {
      const emails = importText.split(/[\n,;]/).map(e => e.trim()).filter(e => e.includes('@'));
      if (emails.length === 0) {
        toast.error('Nessuna email valida trovata');
        return;
      }
      const response = await fetch(`${API_URL}/api/newsletter/import`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ emails, source: 'import' })
      });
      const data = await response.json();
      if (data.success) {
        toast.success(`${data.imported} email importate, ${data.skipped} gia' esistenti`);
        setImportText('');
        fetchStats();
        fetchSubscribers();
      }
    } catch (error) {
      toast.error('Errore importazione');
    } finally {
      setImporting(false);
    }
  };

  const handlePreview = async () => {
    try {
      const response = await fetch(`${API_URL}/api/newsletter/preview`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ subject: emailSubject, content: emailContent, template: selectedTemplate })
      });
      const data = await response.json();
      setPreviewHtml(data.html);
      setShowPreview(true);
    } catch (error) {
      toast.error('Errore caricamento anteprima');
    }
  };

  const handleTestSend = async () => {
    if (!testEmail.includes('@')) {
      toast.error('Inserisci un indirizzo email valido');
      return;
    }
    if (selectedTemplate === 'custom' && !emailContent.trim()) {
      toast.error('Il contenuto è vuoto');
      return;
    }
    setTestSending(true);
    try {
      const response = await fetch(`${API_URL}/api/newsletter/test-send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ to: testEmail.trim(), subject: emailSubject, content: emailContent, template: selectedTemplate })
      });
      const data = await response.json();
      if (response.ok && data.success) toast.success(data.message);
      else toast.error(typeof data.detail === 'string' ? data.detail : 'Invio di prova fallito');
    } catch (error) {
      toast.error('Invio di prova fallito');
    } finally {
      setTestSending(false);
    }
  };

  const pollCampaignProgress = (campaignId) => {
    pollRef.current = setInterval(async () => {
      try {
        const response = await fetch(`${API_URL}/api/newsletter/campaign/${campaignId}`);
        const data = await response.json();
        setActiveCampaign(data);
        if (data.status === 'completed' || data.status === 'completed_with_errors' || data.status === 'error') {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setSending(false);
          fetchCampaigns();
          if (data.status === 'completed') {
            toast.success(`Newsletter inviata con successo a ${data.sent} destinatari!`);
          } else if (data.status === 'completed_with_errors') {
            toast.success(`Invio completato: ${data.sent} inviate, ${data.failed} fallite`);
          } else {
            toast.error(`Errore durante l'invio: ${data.error || 'Errore sconosciuto'}`);
          }
        }
      } catch (error) {
      }
    }, 3000);
  };

  const handleSend = async () => {
    if (selectedTemplate === 'custom' && (!emailSubject || !emailContent)) {
      toast.error('Compila soggetto e contenuto');
      return;
    }
    if (selectedTemplate === 'new_site' && !emailSubject) {
      setEmailSubject("ALLMX - Il Nostro Nuovo Sito E' Online!");
    }

    const confirmMsg = `Vuoi inviare la newsletter a ${stats?.active || 0} iscritti?`;
    if (!window.confirm(confirmMsg)) return;

    setSending(true);
    setActiveCampaign(null);

    try {
      const response = await fetch(`${API_URL}/api/newsletter/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          subject: emailSubject || "ALLMX - Il Nostro Nuovo Sito E' Online!",
          content: emailContent,
          template: selectedTemplate,
          recipients: 'all'
        })
      });
      const data = await response.json();
      if (response.ok && data.success) {
        toast.success(`Invio avviato a ${data.recipient_count} destinatari`);
        pollCampaignProgress(data.campaign_id);
      } else {
        toast.error(data.message || data.detail || 'Errore invio');
        setSending(false);
      }
    } catch (error) {
      toast.error('Errore invio newsletter');
      setSending(false);
    }
  };

  const handleUnsubscribe = async (email) => {
    if (!window.confirm(`Disattivare ${email}?`)) return;
    try {
      await fetch(`${API_URL}/api/newsletter/unsubscribe/${email}`, { method: 'POST' });
      toast.success('Email disattivata');
      fetchSubscribers();
      fetchStats();
    } catch (error) {
      toast.error('Errore');
    }
  };

  return (
    <div className="min-h-screen" style={{ background: 'var(--bg-primary)', color: 'var(--text-primary)' }}>
      <div className="max-w-7xl mx-auto p-6 md:p-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <button onClick={() => navigate('/admin/inventory')} className="flex items-center gap-2 text-sm hover:text-[var(--brand-primary)] transition-colors" style={{ color: 'var(--text-muted)' }}>
              <ArrowLeft size={18} /> Inventario
            </button>
            <button onClick={() => navigate('/admin/import')} className="btn-secondary text-sm py-2 px-4">Import</button>
            <button onClick={() => navigate('/admin/orders')} className="btn-secondary text-sm py-2 px-4">Ordini</button>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold mb-2" data-testid="newsletter-title">
            <Mail className="inline mr-3 mb-1" size={32} /> Gestione Newsletter
          </h1>
          <p style={{ color: 'var(--text-muted)' }}>Gestisci iscritti e invia comunicazioni ai clienti</p>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            {[
              { label: 'Totale Iscritti', value: stats.total, color: 'var(--brand-primary)', icon: Users },
              { label: 'Attivi', value: stats.active, color: '#22c55e', icon: TrendingUp },
              { label: 'Da eBay', value: stats.by_source?.ebay_clienti || 0, color: '#3b82f6', icon: Mail },
              { label: 'Da Ordini', value: stats.by_source?.order || 0, color: '#a855f7', icon: Mail },
            ].map((s) => (
              <div key={s.label} className="dark-card p-5" data-testid={`stat-card-${s.label}`}>
                <p className="text-sm mb-1" style={{ color: 'var(--text-muted)' }}>{s.label}</p>
                <p className="text-2xl md:text-3xl font-bold" style={{ color: s.color }}>{s.value}</p>
              </div>
            ))}
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-1 mb-6 border-b" style={{ borderColor: 'var(--border-primary)' }}>
          {[
            { id: 'subscribers', label: `Iscritti (${subscribers.length})`, icon: Users },
            { id: 'import', label: 'Importa', icon: Upload },
            { id: 'compose', label: 'Invia Newsletter', icon: Send },
            { id: 'history', label: 'Storico Invii', icon: Clock },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              data-testid={`tab-${tab.id}`}
              className={`flex items-center gap-2 px-4 py-3 font-semibold text-sm transition-colors border-b-2 ${
                activeTab === tab.id
                  ? 'text-[var(--brand-primary)] border-[var(--brand-primary)]'
                  : 'border-transparent hover:text-[var(--brand-primary)]'
              }`}
              style={activeTab !== tab.id ? { color: 'var(--text-muted)' } : {}}
            >
              <tab.icon size={16} /> {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="dark-card p-6">

          {/* === SUBSCRIBERS TAB === */}
          {activeTab === 'subscribers' && (
            <div>
              <h2 className="text-xl font-bold mb-4">Lista Iscritti</h2>
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="animate-spin mr-2" size={24} /> Caricamento...
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full" data-testid="subscribers-table">
                    <thead>
                      <tr style={{ borderBottom: '1px solid var(--border-primary)' }}>
                        <th className="text-left py-3 px-4 text-sm font-semibold" style={{ color: 'var(--text-muted)' }}>#</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold" style={{ color: 'var(--text-muted)' }}>Email</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold" style={{ color: 'var(--text-muted)' }}>Fonte</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold" style={{ color: 'var(--text-muted)' }}>Data</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold" style={{ color: 'var(--text-muted)' }}>Stato</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold" style={{ color: 'var(--text-muted)' }}>Azioni</th>
                      </tr>
                    </thead>
                    <tbody>
                      {subscribers.map((sub, index) => (
                        <tr key={sub.email} className="hover:bg-white/5" style={{ borderBottom: '1px solid var(--border-secondary)' }}>
                          <td className="py-3 px-4 text-sm" style={{ color: 'var(--text-muted)' }}>{index + 1}</td>
                          <td className="py-3 px-4 text-sm font-medium">{sub.email}</td>
                          <td className="py-3 px-4">
                            <span className="px-2 py-1 text-xs rounded" style={{ background: 'rgba(59,130,246,0.15)', color: '#60a5fa' }}>
                              {sub.source}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-sm" style={{ color: 'var(--text-muted)' }}>
                            {new Date(sub.subscribed_at).toLocaleDateString('it-IT')}
                          </td>
                          <td className="py-3 px-4">
                            <span className={`px-2 py-1 text-xs rounded ${sub.active ? 'bg-green-500/15 text-green-400' : 'bg-red-500/15 text-red-400'}`}>
                              {sub.active ? 'Attivo' : 'Disattivato'}
                            </span>
                          </td>
                          <td className="py-3 px-4">
                            {sub.active && (
                              <button onClick={() => handleUnsubscribe(sub.email)} className="text-xs text-red-400 hover:text-red-300 transition-colors">
                                Disattiva
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {subscribers.length === 0 && (
                    <p className="text-center py-8" style={{ color: 'var(--text-muted)' }}>Nessun iscritto</p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* === IMPORT TAB === */}
          {activeTab === 'import' && (
            <div>
              <h2 className="text-xl font-bold mb-4">Importa Email</h2>
              <p className="mb-4 text-sm" style={{ color: 'var(--text-muted)' }}>
                Inserisci una lista di email (una per riga, separate da virgola o punto e virgola)
              </p>
              <textarea
                value={importText}
                onChange={(e) => setImportText(e.target.value)}
                placeholder={"email1@example.com\nemail2@example.com\nemail3@example.com"}
                data-testid="import-textarea"
                className="w-full h-64 rounded p-4 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--brand-primary)]"
                style={{ background: 'var(--bg-tertiary)', color: 'var(--text-primary)', border: '1px solid var(--border-primary)' }}
              />
              <button onClick={handleImport} disabled={importing} className="btn-primary mt-4 flex items-center gap-2" data-testid="import-btn">
                {importing ? <Loader2 className="animate-spin" size={18} /> : <Upload size={18} />}
                {importing ? 'Importazione...' : 'Importa Email'}
              </button>
            </div>
          )}

          {/* === COMPOSE TAB === */}
          {activeTab === 'compose' && (
            <div>
              <h2 className="text-xl font-bold mb-4">Invia Newsletter</h2>

              {/* Template selector */}
              <div className="mb-6">
                <label className="block text-sm font-semibold mb-3" style={{ color: 'var(--text-secondary)' }}>Scegli Template</label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <button
                    onClick={() => setSelectedTemplate('new_site')}
                    data-testid="template-new-site"
                    className={`p-5 rounded text-left transition-all ${selectedTemplate === 'new_site' ? 'ring-2 ring-[var(--brand-primary)]' : ''}`}
                    style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border-primary)' }}
                  >
                    <h3 className="font-bold text-[var(--brand-primary)] mb-1">Nuovo Sito Online</h3>
                    <p className="text-sm" style={{ color: 'var(--text-muted)' }}>Template professionale per annunciare il lancio di allmx.it</p>
                  </button>
                  <button
                    onClick={() => setSelectedTemplate('custom')}
                    data-testid="template-custom"
                    className={`p-5 rounded text-left transition-all ${selectedTemplate === 'custom' ? 'ring-2 ring-[var(--brand-primary)]' : ''}`}
                    style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border-primary)' }}
                  >
                    <h3 className="font-bold text-[var(--brand-primary)] mb-1">Newsletter Personalizzata</h3>
                    <p className="text-sm" style={{ color: 'var(--text-muted)' }}>Scrivi il tuo contenuto con branding ALLMX</p>
                  </button>
                </div>
              </div>

              {/* Subject */}
              <div className="mb-4">
                <label className="block text-sm font-semibold mb-2" style={{ color: 'var(--text-secondary)' }}>Oggetto Email</label>
                <input
                  type="text"
                  value={emailSubject}
                  onChange={(e) => setEmailSubject(e.target.value)}
                  placeholder={selectedTemplate === 'new_site' ? "ALLMX - Il Nostro Nuovo Sito E' Online!" : "es: Nuove Offerte ALLMX"}
                  data-testid="email-subject-input"
                  className="w-full rounded p-3 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--brand-primary)]"
                  style={{ background: 'var(--bg-tertiary)', color: 'var(--text-primary)', border: '1px solid var(--border-primary)' }}
                />
              </div>

              {/* Content (only for custom) */}
              {selectedTemplate === 'custom' && (
                <div className="mb-4">
                  <label className="block text-sm font-semibold mb-2" style={{ color: 'var(--text-secondary)' }}>Contenuto Email</label>
                  <textarea
                    value={emailContent}
                    onChange={(e) => setEmailContent(e.target.value)}
                    placeholder="Scrivi qui il contenuto della tua newsletter..."
                    data-testid="email-content-textarea"
                    className="w-full h-64 rounded p-4 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--brand-primary)]"
                    style={{ background: 'var(--bg-tertiary)', color: 'var(--text-primary)', border: '1px solid var(--border-primary)' }}
                  />
                </div>
              )}

              {/* Preview + Send buttons */}
              <div className="flex flex-wrap items-center gap-4 mt-6">
                <button onClick={handlePreview} className="btn-secondary flex items-center gap-2" data-testid="preview-btn">
                  <Eye size={18} /> Anteprima
                </button>
                <button
                  onClick={handleSend}
                  disabled={sending}
                  className="btn-primary flex items-center gap-2"
                  data-testid="send-newsletter-btn"
                >
                  {sending ? <Loader2 className="animate-spin" size={18} /> : <Send size={18} />}
                  {sending ? 'Invio in corso...' : `Invia a ${stats?.active || 0} Iscritti`}
                </button>
              </div>

              {/* Test send */}
              <div className="mt-6 p-4 rounded" style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border-primary)' }}>
                <label className="block text-sm font-semibold mb-2" style={{ color: 'var(--text-secondary)' }}>Invia email di prova</label>
                <div className="flex flex-col sm:flex-row gap-3">
                  <input
                    type="email"
                    value={testEmail}
                    onChange={(e) => setTestEmail(e.target.value)}
                    placeholder="tua@email.it"
                    data-testid="test-email-input"
                    className="flex-1 rounded p-3 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--brand-primary)]"
                    style={{ background: 'var(--bg-secondary)', color: 'var(--text-primary)', border: '1px solid var(--border-primary)' }}
                  />
                  <button onClick={handleTestSend} disabled={testSending} className="btn-secondary flex items-center justify-center gap-2" data-testid="test-send-btn">
                    {testSending ? <Loader2 className="animate-spin" size={18} /> : <Mail size={18} />}
                    {testSending ? 'Invio...' : 'Invia prova'}
                  </button>
                </div>
                <p className="mt-2 text-xs" style={{ color: 'var(--text-muted)' }}>Controlla la mail di prova prima di inviarla a tutti gli iscritti.</p>
              </div>

              {/* Active campaign progress */}
              {activeCampaign && (
                <div className="mt-6 p-5 rounded" style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border-primary)' }} data-testid="campaign-progress">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-bold text-sm">Progresso Invio</h3>
                    <span className="text-sm" style={{ color: 'var(--text-muted)' }}>
                      {activeCampaign.sent + activeCampaign.failed}/{activeCampaign.recipient_count}
                    </span>
                  </div>
                  <div className="w-full rounded-full h-3 mb-3" style={{ background: 'var(--bg-secondary)' }}>
                    <div
                      className="h-3 rounded-full transition-all duration-500"
                      style={{ width: `${activeCampaign.progress}%`, background: activeCampaign.failed > 0 ? '#f59e0b' : 'var(--brand-primary)' }}
                    />
                  </div>
                  <div className="flex gap-6 text-sm">
                    <span className="text-green-400">
                      <CheckCircle className="inline mr-1" size={14} /> {activeCampaign.sent} inviate
                    </span>
                    {activeCampaign.failed > 0 && (
                      <span className="text-red-400">
                        <XCircle className="inline mr-1" size={14} /> {activeCampaign.failed} fallite
                      </span>
                    )}
                    <span style={{ color: 'var(--text-muted)' }}>
                      {activeCampaign.progress}%
                    </span>
                  </div>
                  {activeCampaign.status === 'completed' && (
                    <p className="mt-3 text-sm text-green-400 font-semibold">Invio completato con successo!</p>
                  )}
                  {activeCampaign.status === 'error' && (
                    <p className="mt-3 text-sm text-red-400">Errore: {activeCampaign.error}</p>
                  )}
                </div>
              )}

              {/* Note about SMTP in preview */}
              <p className="mt-4 text-xs" style={{ color: 'var(--text-muted)' }}>
                Nota: L'invio email potrebbe non funzionare nell'ambiente preview. In produzione su allmx.it funziona correttamente tramite SMTP Aruba.
              </p>
            </div>
          )}

          {/* === HISTORY TAB === */}
          {activeTab === 'history' && (
            <div>
              <h2 className="text-xl font-bold mb-4">Storico Campagne</h2>
              {campaigns.length === 0 ? (
                <p className="text-center py-8" style={{ color: 'var(--text-muted)' }}>Nessuna campagna ancora</p>
              ) : (
                <div className="space-y-3">
                  {campaigns.map((c) => (
                    <div key={c.campaign_id} className="p-4 rounded" style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border-secondary)' }} data-testid={`campaign-${c.campaign_id}`}>
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="font-semibold text-sm">{c.subject}</h3>
                          <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>
                            {new Date(c.created_at).toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                            {c.retry_of && <span className="ml-2 text-yellow-400">(Reinvio)</span>}
                          </p>
                        </div>
                        <div className="flex items-center gap-4 text-sm">
                          <span className="text-green-400">{c.sent} inviate</span>
                          {c.failed > 0 && <span className="text-red-400">{c.failed} fallite</span>}
                          <span className={`px-2 py-1 rounded text-xs font-semibold ${
                            c.status === 'completed' ? 'bg-green-500/15 text-green-400' :
                            c.status === 'sending' ? 'bg-yellow-500/15 text-yellow-400' :
                            c.status === 'completed_with_errors' ? 'bg-orange-500/15 text-orange-400' :
                            'bg-red-500/15 text-red-400'
                          }`}>
                            {c.status === 'completed' ? 'Completata' : c.status === 'sending' ? 'In corso' : c.status === 'completed_with_errors' ? 'Con errori' : 'Errore'}
                          </span>
                        </div>
                      </div>
                      {/* Retry button for failed campaigns */}
                      {c.failed > 0 && (c.status === 'completed_with_errors' || c.status === 'error') && (
                        <div className="mt-3 pt-3" style={{ borderTop: '1px solid var(--border-secondary)' }}>
                          <button
                            onClick={async () => {
                              if (!window.confirm(`Reinviare a ${c.failed} email fallite?`)) return;
                              try {
                                const res = await fetch(`${API_URL}/api/newsletter/retry/${c.campaign_id}`, { method: 'POST' });
                                const data = await res.json();
                                if (data.success) {
                                  toast.success(`Reinvio avviato a ${data.recipient_count} email`);
                                  pollCampaignProgress(data.campaign_id);
                                  setActiveTab('compose');
                                } else {
                                  toast.error(data.message || 'Errore reinvio');
                                }
                              } catch (err) { toast.error('Errore reinvio'); }
                            }}
                            className="text-sm font-semibold text-[var(--brand-primary)] hover:underline"
                            data-testid={`retry-btn-${c.campaign_id}`}
                          >
                            Reinvia a {c.failed} email fallite
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Preview Modal */}
        {showPreview && (
          <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={() => setShowPreview(false)}>
            <div className="bg-white rounded-lg w-full max-w-[650px] max-h-[90vh] overflow-auto" onClick={e => e.stopPropagation()}>
              <div className="sticky top-0 bg-white p-3 border-b flex justify-between items-center z-10">
                <h3 className="font-bold text-gray-800">Anteprima Newsletter</h3>
                <button onClick={() => setShowPreview(false)} className="text-gray-500 hover:text-gray-800 text-xl font-bold px-2">&times;</button>
              </div>
              <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(previewHtml) }} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminNewsletter;
